import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addToEdit,
  removeFromEdit,
  isInEdit,
  getEditItems,
} from "../state/editStore";
import { getActiveEditId, setActiveEditId } from "../state/activeEditStore";
import { listEdits, createEditMeta, renameEditMeta } from "../state/editsStore";
import { rankProductsForQueryAsync } from "../reco/semanticRanker";
import { EDIT_CHANGED_EVENT } from "../state/editEvents";
import { getPublicProducts } from "../lib/catalogApi";
import { useCountry } from "../hooks/useCountry";
import {
  logProductSaved,
  logProductUnsaved,
  logProductAddedToBag,
  logProductRemovedFromBag,
} from "../lib/interactionEventsApi";
import {
  addToFittingQueue,
  removeFromFittingQueue,
  isInFittingQueue,
  FITTING_QUEUE_CHANGED_EVENT,
} from "../state/fittingRoomQueueStore";
import { normalizeBackendProduct } from "../catalog/meshResolver";
import { isTryOnAvailable } from "../catalog/tryOnAvailability";
import {
  addToBag,
  removeFromBag,
  isInBag,
  BAG_CHANGED_EVENT,
} from "../state/bagStore";
import { fetchEditSignals } from "../lib/editApi";
import { optimizedImageUrl } from "../lib/optimizedImageUrl";

export const CATEGORY_OPTIONS = [
  { id: "top", label: "Tops" },
  { id: "bottom", label: "Bottoms" },
  { id: "dress", label: "Dresses" },
  { id: "outer", label: "Outerwear" },
  { id: "shoe", label: "Shoes" },
  { id: "bag", label: "Bags" },
  { id: "hat", label: "Hats" },
  { id: "jewellery", label: "Jewellery" },
  { id: "accessory", label: "Accessories" },
];

export const COLOUR_OPTIONS = [
  "Black",
  "White",
  "Cream",
  "Beige",
  "Brown",
  "Grey",
  "Blue",
  "Navy",
  "Green",
  "Pink",
  "Red",
  "Burgundy",
];

function CartIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        d="M4 6h2l1.2 6.2a1 1 0 0 0 1 .8h7.9a1 1 0 0 0 1-.76L18.4 8H7.1"
        fill="none"
        stroke={active ? "var(--cream)" : "var(--burgundy)"}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="18" r="1.4" fill={active ? "var(--cream)" : "var(--burgundy)"} />
      <circle cx="17" cy="18" r="1.4" fill={active ? "var(--cream)" : "var(--burgundy)"} />
    </svg>
  );
}

function ProductImageCarousel({ item, height = 220, fit = "cover", showThumbsRight = false }) {
  const images = useMemo(() => {
    const arr = [];
    if (Array.isArray(item.images)) arr.push(...item.images);
    if (item.image) arr.push(item.image);
    if (item.backImage) arr.push(item.backImage);
    if (item.imageBack) arr.push(item.imageBack);
    if (item.back) arr.push(item.back);
    return Array.from(new Set(arr.filter(Boolean)));
  }, [item]);

  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [images.length]);

  const hasMany = images.length > 1;

  function next(e) {
    e.stopPropagation();
    setIndex((i) => (i + 1) % images.length);
  }

  function prev(e) {
    e.stopPropagation();
    setIndex((i) => (i - 1 + images.length) % images.length);
  }

  if (!images.length) return null;

  return (
    <div
      style={{
        display: showThumbsRight ? "grid" : "block",
        gridTemplateColumns: showThumbsRight ? "1fr 86px" : undefined,
        gap: showThumbsRight ? 12 : 0,
        alignItems: "stretch",
      }}
    >
      <div
        style={{
          position: "relative",
          borderRadius: showThumbsRight ? 26 : 0,
          overflow: "hidden",
          background: "rgba(251,247,242,0.72)",
          border: showThumbsRight ? "1px solid rgba(255,255,255,0.65)" : "none",
        }}
      >
<img
  src={optimizedImageUrl(images[index], {
    width: showThumbsRight ? 900 : 520,
    quality: 78,
  })}
  alt={item.name}
  loading="lazy"
  decoding="async"
          style={{
            width: "100%",
            height,
            objectFit: fit,
            objectPosition: "center",
            display: "block",
            background: "rgba(251,247,242,0.72)",
          }}
        />

        {hasMany && !showThumbsRight && (
          <>
            <button type="button" onClick={prev} className="carousel-arrow left" aria-label="Previous image">
              ‹
            </button>
            <button type="button" onClick={next} className="carousel-arrow right" aria-label="Next image">
              ›
            </button>
          </>
        )}
      </div>

      {showThumbsRight && hasMany && (
        <div
          style={{
            display: "grid",
            gap: 10,
            alignContent: "start",
            maxHeight: height,
            overflowY: "auto",
            paddingRight: 2,
          }}
        >
          {images.map((src, thumbIndex) => (
            <button
              key={src}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIndex(thumbIndex);
              }}
              style={{
                width: 78,
                height: 92,
                borderRadius: 16,
                overflow: "hidden",
                padding: 0,
                cursor: "pointer",
                background: "rgba(251,247,242,0.9)",
                border:
                  thumbIndex === index
                    ? "2px solid var(--burgundy)"
                    : "1px solid rgba(255,255,255,0.8)",
                boxShadow:
                  thumbIndex === index
                    ? "0 10px 24px rgba(91,38,48,0.18)"
                    : "0 8px 18px rgba(0,0,0,0.06)",
              }}
            >
           <img
  src={optimizedImageUrl(src, { width: 180, quality: 72 })}
  alt=""
  loading="lazy"
  decoding="async"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  display: "block",
                  background: "rgba(251,247,242,0.78)",
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getColourHaystack(product) {
  return [
    product?.name || "",
    ...(Array.isArray(product?.styleTags) ? product.styleTags : []),
    product?.colour || "",
    product?.color || "",
  ]
    .join(" ")
    .toLowerCase();
}

function normalizeSignalText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildProductSignalHaystack(product) {
  return normalizeSignalText(
    [
      product?.name,
      product?.title,
      product?.brand,
      product?.category,
      product?.variant,
      product?.fit,
      product?.budgetTier,
      product?.color,
      product?.colour,
      product?.material,
      product?.description,
      ...(Array.isArray(product?.styleTags) ? product.styleTags : []),
    ].join(" ")
  );
}

function scoreProductForEditSignals(product, editSignals) {
  if (!editSignals) return 0;

  const hay = buildProductSignalHaystack(product);

  const dominantColors = Array.isArray(editSignals?.colors?.dominant)
    ? editSignals.colors.dominant
    : [];

  const accentColors = Array.isArray(editSignals?.colors?.accents)
    ? editSignals.colors.accents
    : [];

  const allColors = Array.isArray(editSignals?.colors?.all)
    ? editSignals.colors.all
    : [];

  const silhouettes = Array.isArray(editSignals?.silhouettes)
    ? editSignals.silhouettes
    : [];

  const styles = Array.isArray(editSignals?.styles)
    ? editSignals.styles
    : [];

  const categories = Array.isArray(editSignals?.categories)
    ? editSignals.categories
    : [];

  const materials = Array.isArray(editSignals?.materials)
    ? editSignals.materials
    : [];

  const intents = Array.isArray(editSignals?.intents)
    ? editSignals.intents
    : [];

  const phrases = Array.isArray(editSignals?.phrases)
    ? editSignals.phrases
    : [];

  let score = 0;

  for (const value of categories) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 7;
  }

  for (const value of styles) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 5;
  }

  for (const value of intents) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 5;
  }

  for (const value of silhouettes) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 4;
  }

  for (const value of materials) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 4;
  }

  for (const value of dominantColors) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 3;
  }

  for (const value of accentColors) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 2;
  }

  for (const value of allColors) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 1;
  }

  for (const value of phrases) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 2;
  }

  return score;
}


function normalizeProductImages(product) {
  const gallery = Array.isArray(product?.images)
    ? product.images.map((img) => (typeof img === "string" ? img : img?.url)).filter(Boolean)
    : [];

  return Array.from(
    new Set([product?.image_url, ...gallery].filter(Boolean))
  );
}

function buildBrowseProduct(rawProduct) {
  const tryOnProduct = normalizeBackendProduct(rawProduct);
  const images = normalizeProductImages(rawProduct);

  return {
    ...tryOnProduct,

    rawProduct,

    title: rawProduct?.title || tryOnProduct.name,
    name: rawProduct?.title || tryOnProduct.name,
    description: rawProduct?.description || "",
    brand: rawProduct?.brand || tryOnProduct.brand || "Unknown Store",

    color: rawProduct?.color || null,
    material: rawProduct?.material || null,

    price_amount: rawProduct?.price_amount || null,
    price: Number(rawProduct?.price_amount || 0),
    currency: rawProduct?.currency || "NZD",

    productUrl: rawProduct?.product_url || tryOnProduct.productUrl || null,
    image: tryOnProduct.image || images[0] || null,
    images,

    variants: rawProduct?.variants || [],

    tryOn: tryOnProduct,
    tryOnAvailable: isTryOnAvailable(tryOnProduct),
  };
}

export default function BrowsePanel({
  open,
  onClose,
  onAddToFittingRoom,
  onAddToEdit,
  onRequestOpenEdit,
}) {
  const [sortMode, setSortMode] = useState("default");
  const [useEditRanking, setUseEditRanking] = useState(true);
  const [editSignals, setEditSignals] = useState(null);
  const [editSignalsLoading, setEditSignalsLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth <= 768);
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

  const [selectedColours, setSelectedColours] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [recoLoading, setRecoLoading] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [countryTick, setCountryTick] = useState(0);
  const { country } = useCountry();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [editVersion, setEditVersion] = useState(0);
  const bumpEditVersion = () => setEditVersion((v) => v + 1);

  const [queueTick, setQueueTick] = useState(0);
  const [bagTick, setBagTick] = useState(0);

  useEffect(() => {
    const onQueueChanged = () => setQueueTick((t) => t + 1);
    window.addEventListener(FITTING_QUEUE_CHANGED_EVENT, onQueueChanged);
    return () => window.removeEventListener(FITTING_QUEUE_CHANGED_EVENT, onQueueChanged);
  }, []);

  useEffect(() => {
    const onBagChanged = () => setBagTick((t) => t + 1);
    window.addEventListener(BAG_CHANGED_EVENT, onBagChanged);
    return () => window.removeEventListener(BAG_CHANGED_EVENT, onBagChanged);
  }, []);

  const [query, setQuery] = useState("");
  const [activeEditId, setActiveEditIdState] = useState(getActiveEditId());
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [categories, setCategories] = useState({});

  const stores = useMemo(() => {
    const map = new Map();

    for (const product of catalogProducts) {
      const brand = (product.brand || "").trim();
      if (!brand) continue;

      const id = brand.toLowerCase().replace(/\s+/g, "-");

      if (!map.has(id)) {
        map.set(id, {
          id,
          name: brand,
          location: country,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogProducts]);

  useEffect(() => {
    setActiveEditIdState(getActiveEditId());
  }, [editVersion]);

  useEffect(() => {
    const onEditChanged = () => {
      setActiveEditIdState(getActiveEditId());
      bumpEditVersion();
    };
    window.addEventListener(EDIT_CHANGED_EVENT, onEditChanged);
    return () => window.removeEventListener(EDIT_CHANGED_EVENT, onEditChanged);
  }, []);

  useEffect(() => {
  const onCountryChanged = () => setCountryTick((t) => t + 1);
  window.addEventListener("veya_country_changed", onCountryChanged);
  return () => window.removeEventListener("veya_country_changed", onCountryChanged);
}, []);

  useEffect(() => {
    let cancelled = false;

    async function loadCatalogProducts() {
  if (!country) return;
      setCatalogLoading(true);
      try {
        const rows = await getPublicProducts(country);
        if (cancelled) return;

const products = Array.isArray(rows) ? rows : rows?.products || [];

const normalized = products.map(buildBrowseProduct);

        console.log(
          "[browse panel] normalized mesh assignment",
          normalized.map((p) => ({
            id: p.id,
            name: p.name,
            category: p.category,
            meshUrl: p.meshUrl,
          }))
        );

        setCatalogProducts(normalized);
      } catch (e) {
        console.error("[browse panel] failed to load public products", e);
        if (!cancelled) setCatalogProducts([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadCatalogProducts();

    return () => {
      cancelled = true;
    };
}, [country, countryTick]);

  const baseProducts = useMemo(() => {
    let list = [...catalogProducts];

    if (selectedStoreId) {
      const selectedStore = stores.find((s) => s.id === selectedStoreId);
      const selectedName = selectedStore?.name || null;

      if (selectedName) {
        list = list.filter((p) => p.brand === selectedName);
      }
    }

    if (Object.keys(categories).length) {
  list = list.filter((p) => categories[p.category] !== false);
}

    if (selectedColours.length) {
      list = list.filter((p) => {
        const hay = getColourHaystack(p);
        return selectedColours.some((colour) =>
          hay.includes(colour.toLowerCase())
        );
      });
    }

    return list;
  }, [catalogProducts, selectedStoreId, stores, categories, selectedColours]);

  const editItems = useMemo(() => getEditItems(activeEditId), [activeEditId, editVersion]);
  const edits = useMemo(() => listEdits(), [editVersion]);

  useEffect(() => {
    let cancelled = false;

    async function loadEditSignals() {
      if (!activeEditId || !useEditRanking) {
        setEditSignals(null);
        return;
      }

      setEditSignalsLoading(true);

      try {
        const data = await fetchEditSignals(activeEditId);
        if (!cancelled) {
          setEditSignals(data || null);
        }
      } catch (e) {
        console.error("[browse panel] failed to load edit signals", e);
        if (!cancelled) {
          setEditSignals(null);
        }
      } finally {
        if (!cancelled) {
          setEditSignalsLoading(false);
        }
      }
    }

    loadEditSignals();

    return () => {
      cancelled = true;
    };
  }, [activeEditId, useEditRanking, editVersion]);

  const toastTimerRef = useRef(null);
  const [toast, setToast] = useState({ show: false, text: "" });

  function showToast(text) {
    setToast({ show: true, text });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast({ show: false, text: "" }), 1100);
  }

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hasQuery = !!query.trim();

    setRecoLoading(false);
    setRecommendedProducts([]);

    async function run() {
      const shouldRankByEdit = useEditRanking && !!activeEditId;

      if (!hasQuery && !shouldRankByEdit) {
        return;
      }

      if (hasQuery) {
        setRecoLoading(true);
        try {
          const sorted = await rankProductsForQueryAsync(
            baseProducts,
            editItems,
            activeEditId,
            query,
            { useEditRanking: false }
          );

          if (!cancelled) setRecommendedProducts(sorted);
        } catch (e) {
          console.error("[browse panel] ranking failed", e);
          if (!cancelled) setRecommendedProducts(baseProducts);
        } finally {
          if (!cancelled) setRecoLoading(false);
        }
        return;
      }

if (shouldRankByEdit) {
  if (!editSignals) {
    return;
  }

  const scored = [...baseProducts].map((product, index) => ({
    product,
    index,
    score: scoreProductForEditSignals(product, editSignals),
  }));

  const hasAnySignalMatch = scored.some((row) => row.score > 0);

  if (!hasAnySignalMatch) {
    if (!cancelled) setRecommendedProducts([]);
    return;
  }

  const sorted = scored
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((row) => row.product);

  if (!cancelled) {
    setRecommendedProducts(sorted);
  }
}
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [query, baseProducts, editItems, activeEditId, useEditRanking, editSignals]);

  const productsToShow = useMemo(() => {
    let list = recommendedProducts.length
      ? [...recommendedProducts]
      : [...baseProducts];

    switch (sortMode) {
      case "az":
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;

      case "za":
        list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;

      case "price_low":
        list.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;

      case "price_high":
        list.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;

      default:
        break;
    }

    return list;
  }, [recommendedProducts, baseProducts, sortMode]);

  function ensureActiveEdit() {
    let id = getActiveEditId();
    if (!id) {
      const meta = createEditMeta({ name: "My Edit" });
      id = meta.id;
      setActiveEditId(id);
    }
    setActiveEditIdState(id);
    bumpEditVersion();
    return id;
  }

  function toggleColour(colour) {
    setSelectedColours((prev) =>
      prev.includes(colour)
        ? prev.filter((c) => c !== colour)
        : [...prev, colour]
    );
  }

  if (!open) return null;

  return (
  <>
    <div className="side-panel left-panel">
      <div className="side-panel-header">
        <h2>Browse</h2>
        <button className="panel-close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="side-panel-body">
        {toast.show ? (
          <div className="bp-toast-wrap">
            <div className="bp-toast">{toast.text}</div>
          </div>
        ) : null}


{isMobile && (
  <button
    type="button"
    onClick={() => setMobileControlsOpen((v) => !v)}
    className="mobile-browse-controls-btn"
    style={{ marginBottom: 10 }}
  >
    Browse Controls {mobileControlsOpen ? "×" : "▾"}
  </button>
)}

{(!isMobile || mobileControlsOpen) && (
  <div style={{ margin: "4px 0 8px" }}>
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
    alignItems: "end",
  }}
>
            <div>
              <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Store</div>
              <select
                value={selectedStoreId || ""}
                onChange={(e) => setSelectedStoreId(e.target.value)}
                style={{
                  width: "100%",
                  borderRadius: 12,
                  padding: "8px 10px",
                  border: "1px solid rgba(43,43,43,0.12)",
                  background: "rgba(251,247,242,0.70)",
                }}
              >
                <option value="">All stores</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Edit</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <select
                  value={activeEditId || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setActiveEditId(v);
                    setActiveEditIdState(v);
                    bumpEditVersion();
                  }}
                  style={{
                    flex: 1,
                    borderRadius: 12,
                    padding: "8px 10px",
                    border: "1px solid rgba(43,43,43,0.12)",
                    background: "rgba(251,247,242,0.70)",
                  }}
                >
                  {edits.length ? (
                    edits.map((ed) => (
                      <option key={ed.id} value={ed.id}>
                        {(ed.name || "").trim() || "Untitled Edit"}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No edits yet
                    </option>
                  )}
                </select>

        <label
  style={{
    display: "flex",
    gap: 4,
    alignItems: "center",
    fontSize: 11,
    whiteSpace: "nowrap",
    marginLeft: 2,
  }}
>
                  <input
                    type="checkbox"
                    checked={useEditRanking}
                    onChange={(e) => setUseEditRanking(e.target.checked)}
                  />
                  Use Edit
                </label>
              </div>

              {useEditRanking && editSignalsLoading ? (
<div className="public-muted" style={{ fontSize: 11, marginTop: 4 }}>
  Loading edit signal…
</div>
              ) : null}

              {useEditRanking && activeEditId && editSignals && !editSignalsLoading ? (
  <div className="public-muted" style={{ fontSize: 11, marginTop: 4 }}>
    Browse is being shaped by this Edit.
  </div>
) : null}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Search</div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g. trench, burgundy, minimal, wide leg…"
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      padding: "8px 10px",
                      border: "1px solid rgba(43,43,43,0.12)",
                      background: "rgba(251,247,242,0.70)",
                    }}
                  />
                  {!!query.trim() && (
                    <button
                      className="ghost-btn"
                      style={{ borderRadius: 12, padding: "10px 12px" }}
                      onClick={() => setQuery("")}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {recoLoading ? (
                  <div className="public-muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Searching…
                  </div>
                ) : null}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Sort</div>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    padding: "8px 10px",
                    border: "1px solid rgba(43,43,43,0.12)",
                    background: "rgba(251,247,242,0.70)",
                  }}
                >
                  <option value="default">Default</option>
                  <option value="az">A → Z</option>
                  <option value="za">Z → A</option>
                  <option value="price_low">Price low → high</option>
                  <option value="price_high">Price high → low</option>
                </select>
              </div>

              <div style={{ width: 140 }}>
                <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Filters</div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    padding: "8px 10px",
                    border: "1px solid rgba(43,43,43,0.12)",
                    background: "rgba(251,247,242,0.70)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  Filters ▾
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                className="ghost-btn"
                style={{ borderRadius: 12, padding: "8px 12px" }}
                onClick={() => {
                  const name = window.prompt("Name your new Edit:", "My Edit");
                  const meta = createEditMeta({ name: name || "My Edit" });
                  setActiveEditId(meta.id);
                  setActiveEditIdState(meta.id);
                  onRequestOpenEdit?.();
                  bumpEditVersion();
                }}
                title="Create a new Edit"
              >
                New
              </button>

              <button
                className="ghost-btn"
                style={{ borderRadius: 12, padding: "8px 12px" }}
                onClick={() => {
                  if (!activeEditId) return;
                  const cur = edits.find((e) => e.id === activeEditId);
                  const name = window.prompt("Rename Edit:", cur?.name || "Untitled Edit");
                  if (!name) return;
                  renameEditMeta(activeEditId, name);
                  bumpEditVersion();
                }}
                title="Rename the current Edit"
              >
                Rename
              </button>
            </div>
          </div>

          <div style={{ height: 1, background: "rgba(43,43,43,0.08)", margin: "10px 0" }} />

{filtersOpen && (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 12,
      padding: 10,
      borderRadius: 12,
      background: "rgba(251,247,242,0.72)",
      border: "1px solid rgba(43,43,43,0.08)",
      marginBottom: 8,
    }}
  
            >
              <div>
                <div
style={{
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
}}
                >
                  Store
                </div>
                <label style={{ display: "block", marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={!selectedStoreId}
                    onChange={() => setSelectedStoreId("")}
                  />{" "}
                  All stores
                </label>
                {stores.map((s) => (
                  <label key={s.id} style={{ display: "block", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={selectedStoreId === s.id}
                      onChange={() => setSelectedStoreId(s.id)}
                    />{" "}
                    {s.name}
                  </label>
                ))}
              </div>


              <div>
                <div
style={{
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
}}
                >
                  Category
                </div>
                {CATEGORY_OPTIONS.map((c) => (
                  <label key={c.id} style={{ display: "block", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={categories[c.id] !== false}
                      onChange={(e) =>
                        setCategories((prev) => ({
                          ...prev,
                          [c.id]: e.target.checked,
                        }))
                      }
                    />{" "}
                    {c.label}
                  </label>
                ))}
              </div>

              <div>
                <div
style={{
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
}}
                >
                  Colour
                </div>
                {COLOUR_OPTIONS.map((colour) => (
                  <label key={colour} style={{ display: "block", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={selectedColours.includes(colour)}
                      onChange={() => toggleColour(colour)}
                    />{" "}
                    {colour}
                  </label>
                ))}
              </div>
            </div>
          )}
                </div>
)}

        {catalogLoading ? (
          <div className="public-muted" style={{ fontSize: 12, marginBottom: 10 }}>
            Loading products…
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 10,
          }}
        >
          {productsToShow.map((item) => {
            const inEdit = isInEdit(item.id, activeEditId);
            const queued = isInFittingQueue(item.id);
            const inBag = isInBag(item.id);
            const tryOnAvailable = item.tryOnAvailable;
            void queueTick;
            void bagTick;

            return (
  <div
    key={item.id}
    className="product-card bp-product-card"
    onClick={() => setSelectedProduct(item)}
    role="button"
    tabIndex={0}
style={{
  borderRadius: isMobile ? 14 : 16,
  overflow: "hidden",
  background: "rgba(255,255,255,0.22)",
  border: "1px solid rgba(255,255,255,0.55)",
  backdropFilter: "blur(18px) saturate(1.12)",
  WebkitBackdropFilter: "blur(18px) saturate(1.12)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.55), 0 14px 32px rgba(0,0,0,0.06)",
  display: "flex",
  flexDirection: "column",
}}
              >
                <div style={{ position: "relative" }}>
                  <ProductImageCarousel item={item} height={isMobile ? 150 : 180} />

                  <div
                    style={{
                      position: "absolute",
                      bottom: 8,
                      left: 10,
                      fontSize: 9,
                      fontWeight: 700,
                      background: "rgba(251,247,242,0.85)",
                      padding: "3px 6px",
                      borderRadius: 999,
                      border: "1px solid rgba(43,43,43,0.08)",
                    }}
                  >
                    {item.brand}
                  </div>

                  <button
                    type="button"
                    className={`bp-save-btn bp-save-wrap ${inBag ? "active" : ""}`}
                    aria-label="Add to Bag"
                    onClick={(e) => {
                      e.stopPropagation();

                      if (inBag) {
                        removeFromBag(item.id);

                        logProductRemovedFromBag({
                          productId: item.id,
                          editId: activeEditId || null,
                          surface: "browse_panel",
                          category: item.category,
                          variant: item.variant || null,
                        });

                        showToast("Removed from Bag");
                      } else {
                        addToBag(item);

                        logProductAddedToBag({
                          productId: item.id,
                          editId: activeEditId || null,
                          surface: "browse_panel",
                          category: item.category,
                          variant: item.variant || null,
                        });

                        showToast("Added to Bag");
                      }

                      setBagTick((t) => t + 1);
                    }}
                  >
                    <CartIcon active={inBag} />
                    <span className="veya-tooltip bp-save-tooltip">
                      {inBag ? "Remove from Bag" : "Add to Bag"}
                    </span>
                  </button>

                  {tryOnAvailable && (
                    <button
                      type="button"
                      className={`bp-save-btn bp-save-wrap bp-fitting-btn ${queued ? "active" : ""}`}
                      aria-label="Add to Fitting Room"
                      onClick={(e) => {
                        e.stopPropagation();

                        if (queued) removeFromFittingQueue(item.id);
                        else addToFittingQueue(item);

                        setQueueTick((t) => t + 1);

                        onAddToFittingRoom?.({ ...item });

                        showToast(queued ? "Removed from Fitting Room" : "Added to Fitting Room");
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24">
                        <path
                          d="M12 6a2 2 0 1 0-2-2"
                          fill="none"
                          stroke={queued ? "var(--cream)" : "var(--burgundy)"}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                        <path
                          d="M4 12l8-4 8 4"
                          fill="none"
                          stroke={queued ? "var(--cream)" : "var(--burgundy)"}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 12h16"
                          fill="none"
                          stroke={queued ? "var(--cream)" : "var(--burgundy)"}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>

                      <span className="veya-tooltip bp-save-tooltip">
                        Add to Fitting Room
                      </span>
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={(e) => {
  e.stopPropagation();
                    let effectiveEditId = activeEditId;
                    if (!effectiveEditId) effectiveEditId = ensureActiveEdit();

                    if (inEdit) {
                      removeFromEdit(item.id, effectiveEditId);

                      logProductUnsaved({
                        productId: item.id,
                        editId: effectiveEditId,
                        surface: "browse_panel",
                        category: item.category,
                        variant: item.variant || null,
                      });

                      showToast("Removed from Edit");
                    } else {
                      addToEdit(
                        {
                          kind: "pinned_product",
                          productId: item.id,
                          title: item.name,
                          imageUrl: item.image,
                          sourceUrl: item.productUrl || null,
                          createdAt: Date.now(),
                          category: item.category,
                          variant: item.variant || null,
                          styleTags: item.styleTags || [],
                          fit: item.fit || null,
                          budgetTier: item.budgetTier || null,
                        },
                        effectiveEditId
                      );

                      logProductSaved({
                        productId: item.id,
                        editId: effectiveEditId,
                        surface: "browse_panel",
                        category: item.category,
                        variant: item.variant || null,
                      });

                      onAddToEdit?.({
                        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                        createdAt: Date.now(),
                        kind: "pinned_product",
                        productId: item.id,
                        title: item.name,
                        imageUrl: item.image,
                        sourceUrl: item.productUrl || null,
                        category: item.category,
                        variant: item.variant || null,
                        styleTags: item.styleTags || [],
                        fit: item.fit || null,
                        budgetTier: item.budgetTier || null,
                      });

                      showToast("Saved to Edit");
                    }

                    bumpEditVersion();
                  }}
 style={{
  width: "100%",
  padding: isMobile ? "7px 8px" : "8px 12px",
  border: "none",
  background: "rgba(251,247,242,0.95)",
  borderTop: "1px solid rgba(43,43,43,0.08)",
  fontWeight: 600,
  fontSize: isMobile ? 11 : 12,
  cursor: "pointer",
}}
                >
                  {inEdit ? "Saved" : "Save to Edit"}
                </button>
              </div>
            );
          })}
        </div>

        {!productsToShow.length ? (
          <p className="public-muted" style={{ marginTop: 14 }}>
            No products match your filters.
          </p>
        ) : null}
      </div>

      <div className="side-panel-footer">
        <button className="ghost-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>

{selectedProduct && (() => {
  const detailInEdit = isInEdit(selectedProduct.id, activeEditId);
  const detailInBag = isInBag(selectedProduct.id);
  const detailQueued = isInFittingQueue(selectedProduct.id);
  const detailTryOnAvailable = selectedProduct.tryOnAvailable;

  function handleDetailSaveToEdit() {
    let effectiveEditId = activeEditId;
    if (!effectiveEditId) effectiveEditId = ensureActiveEdit();

    if (detailInEdit) {
      removeFromEdit(selectedProduct.id, effectiveEditId);

      logProductUnsaved({
        productId: selectedProduct.id,
        editId: effectiveEditId,
        surface: "browse_panel_detail",
        category: selectedProduct.category,
        variant: selectedProduct.variant || null,
      });

      showToast("Removed from Edit");
    } else {
      addToEdit(
        {
          kind: "pinned_product",
          productId: selectedProduct.id,
          title: selectedProduct.name,
          imageUrl: selectedProduct.image,
          sourceUrl: selectedProduct.productUrl || null,
          createdAt: Date.now(),
          category: selectedProduct.category,
          variant: selectedProduct.variant || null,
          styleTags: selectedProduct.styleTags || [],
          fit: selectedProduct.fit || null,
          budgetTier: selectedProduct.budgetTier || null,
        },
        effectiveEditId
      );

      logProductSaved({
        productId: selectedProduct.id,
        editId: effectiveEditId,
        surface: "browse_panel_detail",
        category: selectedProduct.category,
        variant: selectedProduct.variant || null,
      });

      showToast("Saved to Edit");
    }

    bumpEditVersion();
  }

  function handleDetailBag() {
    if (detailInBag) {
      removeFromBag(selectedProduct.id);

      logProductRemovedFromBag({
        productId: selectedProduct.id,
        editId: activeEditId || null,
        surface: "browse_panel_detail",
        category: selectedProduct.category,
        variant: selectedProduct.variant || null,
      });

      showToast("Removed from Bag");
    } else {
      addToBag(selectedProduct);

      logProductAddedToBag({
        productId: selectedProduct.id,
        editId: activeEditId || null,
        surface: "browse_panel_detail",
        category: selectedProduct.category,
        variant: selectedProduct.variant || null,
      });

      showToast("Added to Bag");
    }

    setBagTick((t) => t + 1);
  }

  function handleDetailFittingRoom() {
    if (!detailTryOnAvailable) return;

    if (detailQueued) {
      removeFromFittingQueue(selectedProduct.id);
      showToast("Removed from Fitting Room");
    } else {
      addToFittingQueue(selectedProduct);
      onAddToFittingRoom?.({ ...selectedProduct });
      showToast("Added to Fitting Room");
    }

    setQueueTick((t) => t + 1);
  }

  return (
    <div className="side-panel right-panel">
      <div className="side-panel-header">
        <h2>Product Details</h2>
        <button className="panel-close" onClick={() => setSelectedProduct(null)}>
          ✕
        </button>
      </div>

      <div className="side-panel-body">
        <div
          style={{
            borderRadius: 22,
            overflow: "hidden",
            background: "rgba(255,255,255,0.24)",
            border: "1px solid rgba(255,255,255,0.62)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.62), 0 18px 44px rgba(0,0,0,0.08)",
            marginBottom: 14,
            padding: 8,
          }}
        >
          <ProductImageCarousel
            item={selectedProduct}
            height={320}
            fit="contain"
            showThumbsRight
          />
        </div>

        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            opacity: 0.65,
            marginBottom: 6,
          }}
        >
          {selectedProduct.brand}
        </div>

        <h2
          style={{
            margin: "0 0 8px",
            fontSize: 22,
            lineHeight: 1.2,
          }}
        >
          {selectedProduct.name}
        </h2>

        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
          {selectedProduct.currency || "NZD"} ${selectedProduct.price_amount || selectedProduct.price}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <button type="button" className="ghost-btn product-detail-cream-btn" onClick={handleDetailSaveToEdit}>
            {detailInEdit ? "Saved to Edit" : "Save to Edit"}
          </button>

          <button type="button" className="ghost-btn product-detail-cream-btn" onClick={handleDetailBag}>
            {detailInBag ? "Remove from Bag" : "Add to Bag"}
          </button>

          {detailTryOnAvailable && (
            <button type="button" className="ghost-btn product-detail-cream-btn" onClick={handleDetailFittingRoom}>
              {detailQueued ? "In Fitting Room" : "Add to Fitting Room"}
            </button>
          )}

          {selectedProduct.productUrl && (
            <a
              href={selectedProduct.productUrl}
              target="_blank"
              rel="noreferrer"
              className="ghost-btn product-detail-cream-btn"
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              View on Store
            </a>
          )}
        </div>

        {selectedProduct.variants?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Options</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {selectedProduct.variants.map((v) => (
                <span
                  key={v.id || `${v.option1_value}-${v.option2_value}`}
                  style={{
                    padding: "7px 10px",
                    borderRadius: 999,
                    background: "rgba(251,247,242,0.9)",
                    border: "1px solid rgba(43,43,43,0.10)",
                    fontSize: 12,
                  }}
                >
                  {[v.option1_value, v.option2_value, v.option3_value]
                    .filter(Boolean)
                    .join(" / ")}
                </span>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            lineHeight: 1.65,
            fontSize: 14,
            opacity: 0.9,
            marginBottom: 18,
          }}
        >
          {selectedProduct.description || "No description available."}
        </div>

        <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
          {selectedProduct.color && (
            <div>
              <strong>Colour:</strong> {selectedProduct.color}
            </div>
          )}

          {selectedProduct.material && (
            <div>
              <strong>Material:</strong> {selectedProduct.material}
            </div>
          )}

          {selectedProduct.category && (
            <div>
              <strong>Category:</strong> {selectedProduct.category}
            </div>
          )}

          {!detailTryOnAvailable && (
            <div className="public-muted" style={{ fontSize: 12 }}>
              Try-on is not available for this item.
            </div>
          )}
        </div>
      </div>
    </div>
  );
})()}
  </>
);
}